import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
} from 'react-native';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { useToast } from '@/contexts/admin/ToastContext';
import { extractErrorMessage } from '@/utils/common/errorHandling';
import {
  useAddCreditCardMutation,
  useGenerateCardConnectTokenMutation,
} from '@/store/api';
import { PaymentModalFooter } from '@/components/residents/payments/mobile/makePayment';
import { FloatingLabelInput, PaymentCheckbox } from './shared';

interface CreditCardFormContentProps {
  onCardAdded: (methodId?: number) => void;
  onBack: () => void;
}

interface FormErrors {
  cardholderName?: string;
  cardNumber?: string;
  expiry?: string;
  cvv?: string;
}

/**
 * Credit Card Form Content - Handles card input and tokenization
 */
export const CreditCardFormContent: React.FC<CreditCardFormContentProps> = ({
  onCardAdded,
  onBack,
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();
  const { showSuccess, showError } = useToast();

  // Form state
  const [cardholderName, setCardholderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // API mutations
  const [generateToken, { isLoading: tokenizing }] = useGenerateCardConnectTokenMutation();
  const [addCard, { isLoading: addingCard }] = useAddCreditCardMutation();

  const isLoading = tokenizing || addingCard;

  // Format card number with spaces
  const formatCardNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    const groups = digits.match(/.{1,4}/g);
    return groups ? groups.join(' ') : digits;
  };

  // Format expiry as MM/YY
  const formatExpiry = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length >= 2) {
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
    }
    return digits;
  };

  const handleCardNumberChange = useCallback((value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    setCardNumber(formatCardNumber(digits));
    setErrors((prev) => ({ ...prev, cardNumber: undefined }));
  }, []);

  const handleExpiryChange = useCallback((value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    setExpiry(formatExpiry(digits));
    setErrors((prev) => ({ ...prev, expiry: undefined }));
  }, []);

  const handleCvvChange = useCallback((value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    setCvv(digits);
    setErrors((prev) => ({ ...prev, cvv: undefined }));
  }, []);

  const handleCardholderNameChange = useCallback((value: string) => {
    setCardholderName(value);
    setErrors((prev) => ({ ...prev, cardholderName: undefined }));
  }, []);

  // Validate form
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!cardholderName.trim()) {
      newErrors.cardholderName = 'Cardholder name is required';
    }

    const cardDigits = cardNumber.replace(/\D/g, '');
    if (!cardDigits || cardDigits.length < 13 || cardDigits.length > 16) {
      newErrors.cardNumber = 'Please enter a valid card number';
    }

    const expiryDigits = expiry.replace(/\D/g, '');
    if (!expiryDigits || expiryDigits.length !== 4) {
      newErrors.expiry = 'Please enter a valid expiry date (MM/YY)';
    } else {
      const month = parseInt(expiryDigits.slice(0, 2), 10);
      const year = parseInt(`20${expiryDigits.slice(2, 4)}`, 10);
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;

      if (month < 1 || month > 12) {
        newErrors.expiry = 'Invalid month';
      } else if (year < currentYear || (year === currentYear && month < currentMonth)) {
        newErrors.expiry = 'Card has expired';
      }
    }

    if (!cvv || cvv.length < 3) {
      newErrors.cvv = 'Please enter a valid CVV';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [cardholderName, cardNumber, expiry, cvv]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const cardDigits = cardNumber.replace(/\D/g, '');
      const expiryFormatted = expiry.replace('/', '');

      // Step 1: Tokenize card with CardConnect
      const tokenResponse = await generateToken({
        account: cardDigits,
        cvv,
        expiry: expiryFormatted,
      }).unwrap();

      if (!tokenResponse.token) {
        throw new Error(tokenResponse.message || 'Failed to tokenize card');
      }

      // Step 2: Add card to account
      const addResponse = await addCard({
        name: cardholderName,
        token: tokenResponse.token,
        expiry: expiryFormatted,
        isDefault: makeDefault,
      }).unwrap();

      showSuccess('Card added successfully');
      
      const newMethodId = addResponse?.response?.id || addResponse?.id;
      onCardAdded(newMethodId);
    } catch (error: any) {
      showError(extractErrorMessage(error, 'Failed to add card'));
    }
  }, [
    validateForm,
    cardNumber,
    expiry,
    cvv,
    cardholderName,
    makeDefault,
    generateToken,
    addCard,
    showSuccess,
    showError,
    onCardAdded,
  ]);

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        {/* Instructions */}
        <Text style={[styles.instructionText, { color: MARKETPLACE_COLORS.textPrimary }]}>
          Please enter your card details here.
        </Text>

        {/* Form Fields */}
        <View style={styles.formContainer}>
          <FloatingLabelInput
            label="Cardholder name"
            value={cardholderName}
            onChangeText={handleCardholderNameChange}
            error={errors.cardholderName}
            autoCapitalize="words"
            autoCorrect={false}
          />

          <FloatingLabelInput
            label="Card number"
            value={cardNumber}
            onChangeText={handleCardNumberChange}
            error={errors.cardNumber}
            keyboardType="number-pad"
            maxLength={19}
          />

          {/* Expiry and CVV Row */}
          <View style={styles.rowInputs}>
            <FloatingLabelInput
              label="Exp. date"
              value={expiry}
              onChangeText={handleExpiryChange}
              error={errors.expiry}
              keyboardType="number-pad"
              maxLength={5}
              containerStyle={styles.halfInput}
            />

            <FloatingLabelInput
              label="CVV/CVC"
              value={cvv}
              onChangeText={handleCvvChange}
              error={errors.cvv}
              keyboardType="number-pad"
              maxLength={4}
              secureTextEntry
              containerStyle={styles.halfInput}
            />
          </View>
        </View>

        {/* Make Default Checkbox */}
        <PaymentCheckbox
          checked={makeDefault}
          onPress={() => setMakeDefault(!makeDefault)}
          label="Make this my default payment method"
        />
      </View>

      {/* Footer */}
      <View style={[styles.footer, { borderTopColor: MARKETPLACE_COLORS.dividerGray }]}>
        <PaymentModalFooter
          onCancel={onBack}
          onProceed={handleSave}
          cancelText="Back"
          proceedText="Save"
          loading={isLoading}
          proceedDisabled={isLoading}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 20,
  },
  instructionText: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
  },
  formContainer: {
    gap: 8,
  },
  rowInputs: {
    flexDirection: 'row',
    gap: 32,
  },
  halfInput: {
    flex: 1,
  },
  footer: {
    borderTopWidth: 1,
  },
});

export default CreditCardFormContent;
